import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Maximize, RefreshCw, AlertTriangle, Wrench, Package, ChevronDown, ChevronUp } from 'lucide-react';
import AssetCard from './AssetCard';
import UpcomingWidget from './UpcomingWidget';
import { API_URL } from '../../config/api';

// ============================================
// SÄÄDETTÄVÄT ARVOT - muokkaa näitä testaukseen
// ============================================
const DASHBOARD_SETTINGS = {
  // Fonttiskaalaus näytön koon mukaan
  fontScaleNormal: 0.9,    // < 1920px (normaali näyttö)
  fontScaleLarge: 1.2,     // 1920-2500px (iso näyttö)
  fontScale4K: 1.4,        // > 2500px (4K näyttö)

  // Kuvan korkeus kortissa (prosentteina, 15-40)
  cardImageHeight: 25,
};
// ============================================

// Pagination settings
const PAGE_ROTATION_INTERVAL = 15000; // 15 seconds

const AssetDashboard = ({ dashboardId, publicToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(60);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 1920);
  const [is4KScreen, setIs4KScreen] = useState(window.innerWidth > 2500);
  const [showUpcomingMobile, setShowUpcomingMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const clockRef = useRef(null);
  const pageRotationRef = useRef(null);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsLargeScreen(window.innerWidth > 1920);
      setIs4KScreen(window.innerWidth > 2500);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine API endpoint
  const fetchUrl = publicToken
    ? `${API_URL}/dashboard/public.php?token=${publicToken}`
    : `${API_URL}/dashboard/data.php?id=${dashboardId}`;

  // Error messages in Finnish
  const errorMessages = {
    INACTIVE: 'Dashboard on poistettu käytöstä',
    NOT_FOUND: 'Dashboardia ei löydy',
    CONFIG_ERROR: 'Asetusvirhe - tarkista tenant-konfiguraatio',
    NETWORK: 'Verkkovirhe - tarkista yhteys',
    DEFAULT: 'Virhe ladattaessa dashboardia'
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(fetchUrl, {
        credentials: publicToken ? 'omit' : 'include'
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errorCode = result.code || 'DEFAULT';
        const errorMsg = errorMessages[errorCode] || result.message || errorMessages.DEFAULT;
        setError({ message: errorMsg, code: errorCode });
        return;
      }

      setData(result);
      setError(null);
      setCountdown(result.config?.refresh_interval || 60);
    } catch (err) {
      setError({ message: errorMessages.NETWORK, code: 'NETWORK' });
    } finally {
      setLoading(false);
    }
  }, [fetchUrl, publicToken]);

  // Initial fetch and setup intervals
  useEffect(() => {
    fetchData();

    // Refresh interval
    const refreshInterval = data?.config?.refresh_interval || 60;
    intervalRef.current = setInterval(fetchData, refreshInterval * 1000);

    // Countdown timer
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) return data?.config?.refresh_interval || 60;
        return prev - 1;
      });
    }, 1000);

    // Clock update
    clockRef.current = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (clockRef.current) clearInterval(clockRef.current);
    };
  }, [fetchData, data?.config?.refresh_interval]);

  // Page rotation for devices
  useEffect(() => {
    if (!data?.devices || !data?.config) return;

    const devicesPerPage = data.config.grid_columns * data.config.grid_rows;
    const totalPages = Math.ceil(data.devices.length / devicesPerPage);

    // Only rotate if there's more than one page
    if (totalPages <= 1) {
      setCurrentPage(0);
      return;
    }

    pageRotationRef.current = setInterval(() => {
      setCurrentPage(prev => (prev + 1) % totalPages);
    }, PAGE_ROTATION_INTERVAL);

    return () => {
      if (pageRotationRef.current) clearInterval(pageRotationRef.current);
    };
  }, [data?.devices?.length, data?.config?.grid_columns, data?.config?.grid_rows]);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error('Fullscreen error:', err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  // Format time
  const formatTime = (date) => {
    return date.toLocaleTimeString('fi-FI', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Get custom scale based on screen size
  const getCustomScale = () => {
    if (is4KScreen) return DASHBOARD_SETTINGS.fontScale4K;
    if (isLargeScreen) return DASHBOARD_SETTINGS.fontScaleLarge;
    return DASHBOARD_SETTINGS.fontScaleNormal;
  };

  // Calculate font scale based on grid size and screen size
  const getFontScale = (config) => {
    if (!config) return 'medium';
    if (isMobile) return 'medium';
    const totalCells = config.grid_columns * config.grid_rows;

    // 4K screens (> 2500px) get even bigger fonts
    if (is4KScreen) {
      if (totalCells <= 6) return 'xxlarge';
      if (totalCells <= 9) return 'xlarge';
      return 'large';
    }

    // Large screens (> 1920px) get bigger fonts
    if (isLargeScreen) {
      if (totalCells <= 6) return 'xlarge';
      if (totalCells <= 9) return 'large';
      return 'medium';
    }

    // Normal screens
    if (totalCells <= 6) return 'large';
    if (totalCells <= 9) return 'medium';
    return 'small';
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: '#0f172a',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#94a3b8'
      }}>
        <RefreshCw size={48} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    const isInactive = error.code === 'INACTIVE';
    return (
      <div style={{
        backgroundColor: '#0f172a',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '1rem',
        padding: '1rem',
        color: isInactive ? '#f59e0b' : '#ef4444'
      }}>
        <AlertTriangle size={isMobile ? 48 : 64} />
        <div style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 'bold', textAlign: 'center' }}>
          {error.message}
        </div>
        {isInactive && (
          <div style={{ color: '#94a3b8', fontSize: '1rem', textAlign: 'center' }}>
            Aktivoi dashboard hallintapaneelista
          </div>
        )}
        {error.code !== 'NOT_FOUND' && (
          <button
            onClick={fetchData}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Yritä uudelleen
          </button>
        )}
      </div>
    );
  }

  const { config, tenant, summary, devices, upcoming } = data;
  const fontScale = getFontScale(config);
  const showUpcoming = config.show_upcoming_widget && upcoming && upcoming.length > 0;

  // Pagination calculations
  const devicesPerPage = config.grid_columns * config.grid_rows;
  const totalPages = Math.ceil(devices.length / devicesPerPage);
  const startIndex = currentPage * devicesPerPage;
  const currentDevices = devices.slice(startIndex, startIndex + devicesPerPage);

  // Mobile Layout
  if (isMobile) {
    return (
      <div style={{
        backgroundColor: '#0f172a',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        color: '#f1f5f9'
      }}>
        {/* Mobile Header */}
        {config.show_summary_header && (
          <div style={{
            backgroundColor: '#1e293b',
            borderBottom: '1px solid #334155',
            padding: '0.75rem 1rem',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            {/* Top row: Tenant & Time */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '0.75rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                {tenant.logo_url && (
                  <img
                    src={tenant.logo_url}
                    alt={tenant.name}
                    style={{ height: '32px', objectFit: 'contain' }}
                  />
                )}
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    {tenant.name}
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                    {config.name}
                  </div>
                </div>
              </div>
              <div style={{
                fontWeight: 'bold',
                fontSize: '1.1rem',
                fontFamily: 'monospace',
                color: '#94a3b8'
              }}>
                {formatTime(currentTime)}
              </div>
            </div>

            {/* Stats row */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              backgroundColor: '#0f172a',
              borderRadius: '8px',
              padding: '0.5rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                flexDirection: 'column'
              }}>
                <Package size={18} style={{ color: '#3b82f6' }} />
                <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                  {summary.total_devices}
                </span>
                <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>kohdetta</span>
              </div>

              <div style={{
                width: '1px',
                height: '40px',
                backgroundColor: '#334155'
              }} />

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                flexDirection: 'column'
              }}>
                <AlertTriangle size={18} style={{ color: summary.active_faults > 0 ? '#ef4444' : '#475569' }} />
                <span style={{
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  color: summary.active_faults > 0 ? '#ef4444' : '#94a3b8'
                }}>
                  {summary.active_faults}
                </span>
                <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>vikaa</span>
              </div>

              <div style={{
                width: '1px',
                height: '40px',
                backgroundColor: '#334155'
              }} />

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                flexDirection: 'column'
              }}>
                <Wrench size={18} style={{ color: summary.in_maintenance > 0 ? '#f59e0b' : '#475569' }} />
                <span style={{
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  color: summary.in_maintenance > 0 ? '#f59e0b' : '#94a3b8'
                }}>
                  {summary.in_maintenance}
                </span>
                <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>huollossa</span>
              </div>
            </div>
          </div>
        )}

        {/* Upcoming Widget Toggle (Mobile) */}
        {showUpcoming && (
          <button
            onClick={() => setShowUpcomingMobile(!showUpcomingMobile)}
            style={{
              backgroundColor: '#1e293b',
              border: 'none',
              borderBottom: '1px solid #334155',
              padding: '0.75rem 1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: '#f1f5f9',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>
              📅 Ajankohtaista ({upcoming.length})
            </span>
            {showUpcomingMobile ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        )}

        {/* Collapsible Upcoming Widget */}
        {showUpcoming && showUpcomingMobile && (
          <div style={{
            backgroundColor: '#1e293b',
            borderBottom: '1px solid #334155',
            maxHeight: '250px',
            overflowY: 'auto'
          }}>
            <UpcomingWidget events={upcoming} fontScale="small" compact />
          </div>
        )}

        {/* Mobile Device List */}
        <div style={{
          flex: 1,
          padding: '1rem',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          {devices.map((device) => (
            <AssetCard
              key={device.id}
              device={device}
              fontScale="medium"
              compact
            />
          ))}
        </div>

        {/* Mobile Footer */}
        <div style={{
          backgroundColor: '#1e293b',
          borderTop: '1px solid #334155',
          padding: '0.75rem 1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.8rem',
          color: '#94a3b8',
          position: 'sticky',
          bottom: 0
        }}>
          <div>
            Päivittyy: {countdown}s
          </div>
          <button
            onClick={fetchData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#334155',
              color: '#f1f5f9',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            <RefreshCw size={16} />
            Päivitä
          </button>
        </div>
      </div>
    );
  }

  // Desktop Layout (original)
  return (
    <div style={{
      backgroundColor: '#0f172a',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      color: '#f1f5f9',
      overflow: 'hidden'
    }}>
      {/* Header */}
      {config.show_summary_header && (
        <div style={{
          backgroundColor: '#1e293b',
          borderBottom: '1px solid #334155',
          padding: is4KScreen ? '2rem 3rem' : isLargeScreen ? '1.25rem 2rem' : '0.75rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          {/* Left: Tenant info */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            {tenant.logo_url && (
              <img
                src={tenant.logo_url}
                alt={tenant.name}
                style={{ height: is4KScreen ? '5vw' : isLargeScreen ? '60px' : '40px', objectFit: 'contain' }}
              />
            )}
            <div>
              <div style={{ fontWeight: 'bold', fontSize: is4KScreen ? '2.5vw' : isLargeScreen ? '2rem' : '1.1rem' }}>
                {tenant.name}
              </div>
              <div style={{ color: '#94a3b8', fontSize: is4KScreen ? '1.8vw' : isLargeScreen ? '1.4rem' : '0.85rem' }}>
                {config.name}
              </div>
            </div>
          </div>

          {/* Center: Summary stats */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: is4KScreen ? '3vw' : isLargeScreen ? '3rem' : '2rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: is4KScreen ? '0.8vw' : '0.5rem'
            }}>
              <Package size={is4KScreen ? 72 : isLargeScreen ? 32 : 20} style={{ color: '#3b82f6' }} />
              <span style={{ fontWeight: 'bold', fontSize: is4KScreen ? '2.5vw' : isLargeScreen ? '2.2rem' : '1.25rem' }}>
                {summary.total_devices}
              </span>
              <span style={{ color: '#94a3b8', fontSize: is4KScreen ? '1.8vw' : isLargeScreen ? '1.6rem' : '1rem' }}>kohdetta</span>
            </div>

            {summary.active_faults > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: is4KScreen ? '0.8vw' : '0.5rem'
              }}>
                <AlertTriangle size={is4KScreen ? 72 : isLargeScreen ? 32 : 20} style={{ color: '#ef4444' }} />
                <span style={{ fontWeight: 'bold', fontSize: is4KScreen ? '2.5vw' : isLargeScreen ? '2.2rem' : '1.25rem', color: '#ef4444' }}>
                  {summary.active_faults}
                </span>
                <span style={{ color: '#94a3b8', fontSize: is4KScreen ? '1.8vw' : isLargeScreen ? '1.6rem' : '1rem' }}>vikaa</span>
              </div>
            )}

            {summary.in_maintenance > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: is4KScreen ? '0.8vw' : '0.5rem'
              }}>
                <Wrench size={is4KScreen ? 72 : isLargeScreen ? 32 : 20} style={{ color: '#f59e0b' }} />
                <span style={{ fontWeight: 'bold', fontSize: is4KScreen ? '2.5vw' : isLargeScreen ? '2.2rem' : '1.25rem', color: '#f59e0b' }}>
                  {summary.in_maintenance}
                </span>
                <span style={{ color: '#94a3b8', fontSize: is4KScreen ? '1.8vw' : isLargeScreen ? '1.6rem' : '1rem' }}>huollossa</span>
              </div>
            )}
          </div>

          {/* Right: Clock */}
          <div style={{
            fontWeight: 'bold',
            fontSize: is4KScreen ? '3vw' : isLargeScreen ? '2.5rem' : '1.5rem',
            fontFamily: 'monospace'
          }}>
            {formatTime(currentTime)}
          </div>
        </div>
      )}

      {/* Main content */}
      <div style={{
        flex: 1,
        display: 'flex',
        padding: '1rem',
        gap: '1rem',
        overflow: 'hidden'
      }}>
        {/* Device grid */}
        <div style={{
          flex: showUpcoming ? '0 0 75%' : 1,
          display: 'grid',
          gridTemplateColumns: `repeat(${config.grid_columns}, 1fr)`,
          gridTemplateRows: `repeat(${config.grid_rows}, 1fr)`,
          gap: fontScale === 'large' ? '1.5rem' : '1rem',
          height: '100%'
        }}>
          {currentDevices.map((device) => (
            <AssetCard
              key={device.id}
              device={device}
              fontScale={fontScale}
              customScale={getCustomScale()}
              customImageHeight={DASHBOARD_SETTINGS.cardImageHeight}
            />
          ))}
        </div>

        {/* Upcoming widget */}
        {showUpcoming && (
          <div style={{
            flex: '0 0 23%',
            minWidth: '200px'
          }}>
            <UpcomingWidget
              events={upcoming}
              fontScale={fontScale}
              customScale={getCustomScale()}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        backgroundColor: '#1e293b',
        borderTop: '1px solid #334155',
        padding: '0.5rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '0.85rem',
        color: '#94a3b8',
        flexShrink: 0
      }}>
        <div>
          Päivitetty: {formatTime(currentTime)} • Seuraava: {countdown}s
        </div>

        {/* Page indicator dots */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {Array.from({ length: totalPages }, (_, i) => (
              <div
                key={i}
                style={{
                  width: currentPage === i ? '24px' : '10px',
                  height: '10px',
                  borderRadius: currentPage === i ? '5px' : '50%',
                  backgroundColor: currentPage === i ? '#fbbf24' : '#475569',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
            <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }}>
              {currentPage + 1}/{totalPages}
            </span>
          </div>
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <span>{devices.length} kohdetta</span>

          <button
            onClick={toggleFullscreen}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.35rem 0.75rem',
              backgroundColor: '#334155',
              color: '#f1f5f9',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            <Maximize size={16} />
            Fullscreen
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetDashboard;
